// -----------------------------------------------------------
// FloraIQ - NewCadastralActionModal
// -----------------------------------------------------------

import { useState } from "react";
import { actionTypes } from "@/mocks/operational";

interface NewCadastralActionModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function NewCadastralActionModal({ isOpen, onClose }: NewCadastralActionModalProps) {
  const [step, setStep] = useState(1);
  const [submitted, setSubmitted] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
    setTimeout(() => {
      setSubmitted(false);
      setStep(1);
      onClose();
    }, 1200);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-[10vh]">
      <div className="absolute inset-0 bg-black/40" onClick={onClose}></div>
      <div className="relative bg-white rounded-card border border-background-200/70 w-full max-w-lg mx-4 shadow-lg max-h-[80vh] overflow-y-auto fade-in">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-background-200/50">
          <div>
            <h3 className="text-base font-bold text-foreground-900 font-heading">Nova Ação de Cadastramento</h3>
            <p className="text-xs text-foreground-500 mt-0.5">Preencha os dados da ação operacional</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-100 transition-colors cursor-pointer">
            <i className="ri-close-line text-foreground-600"></i>
          </button>
        </div>

        {/* Stepper */}
        <div className="flex items-center px-5 pt-4">
          {[1, 2, 3].map((s) => (
            <div key={s} className="flex items-center flex-1 last:flex-none">
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
                step >= s ? "bg-primary-500 text-white" : "bg-background-100 text-foreground-400"
              }`}>
                {s}
              </div>
              {s < 3 && <div className={`flex-1 h-0.5 mx-2 transition-all ${step > s ? "bg-primary-500" : "bg-background-200"}`}></div>}
            </div>
          ))}
        </div>
        <div className="flex px-5 pt-1 pb-3">
          {["Dados Básicos", "Equipe e Equipamentos", "Produtos e Prazos"].map((label, i) => (
            <span key={label} className={`flex-1 text-[10px] text-center ${step >= i + 1 ? "text-primary-600 font-medium" : "text-foreground-400"}`}>
              {label}
            </span>
          ))}
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {step === 1 && (
            <>
              <div>
                <label className="block text-xs font-medium text-foreground-700 mb-1">Nome da Ação *</label>
                <input type="text" className="w-full px-3 py-2 text-sm border border-background-200/70 rounded-lg focus:outline-none focus:border-primary-400 bg-white" placeholder="Ex: CTA Goiânia Centro" required />
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-700 mb-1">Tipo de Ação *</label>
                <select className="w-full px-3 py-2 text-sm border border-background-200/70 rounded-lg focus:outline-none focus:border-primary-400 bg-white" required>
                  <option value="">Selecione...</option>
                  {actionTypes.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-foreground-700 mb-1">Município *</label>
                  <input type="text" className="w-full px-3 py-2 text-sm border border-background-200/70 rounded-lg focus:outline-none focus:border-primary-400 bg-white" defaultValue="Goiânia" required />
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground-700 mb-1">UF *</label>
                  <input type="text" className="w-full px-3 py-2 text-sm border border-background-200/70 rounded-lg focus:outline-none focus:border-primary-400 bg-white" defaultValue="GO" required />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-700 mb-1">Cliente / Contratante</label>
                <input type="text" className="w-full px-3 py-2 text-sm border border-background-200/70 rounded-lg focus:outline-none focus:border-primary-400 bg-white" placeholder="Ex: Prefeitura Municipal" />
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-700 mb-1">Projeto Vinculado</label>
                <input type="text" className="w-full px-3 py-2 text-sm border border-background-200/70 rounded-lg focus:outline-none focus:border-primary-400 bg-white" placeholder="Selecione o projeto..." />
              </div>
            </>
          )}

          {step === 2 && (
            <>
              <div>
                <label className="block text-xs font-medium text-foreground-700 mb-1">Responsável Técnico *</label>
                <input type="text" className="w-full px-3 py-2 text-sm border border-background-200/70 rounded-lg focus:outline-none focus:border-primary-400 bg-white" placeholder="Nome do responsável" required />
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-700 mb-1">Área de Estudo</label>
                <input type="text" className="w-full px-3 py-2 text-sm border border-background-200/70 rounded-lg focus:outline-none focus:border-primary-400 bg-white" placeholder="Ex: Setor Central - 4,8 km²" />
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-700 mb-1">Metodologia Aplicada</label>
                <select className="w-full px-3 py-2 text-sm border border-background-200/70 rounded-lg focus:outline-none focus:border-primary-400 bg-white">
                  <option value="ladm">FloraIQ-LADM (Padrão)</option>
                  <option value="simplificada">Simplificada</option>
                  <option value="completa">Completa com 3D</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-700 mb-1">Equipe Prevista</label>
                <textarea className="w-full px-3 py-2 text-sm border border-background-200/70 rounded-lg focus:outline-none focus:border-primary-400 bg-white resize-none" rows={2} placeholder="Nomes separados por vírgula"></textarea>
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-700 mb-1">Equipamentos Previstos</label>
                <p className="text-xs text-foreground-400 mb-1">Conforme lista padrão do projeto</p>
                <div className="grid grid-cols-2 gap-1.5">
                  {["Drone", "GPS/GNSS", "Tablet", "Câmera", "Laser Scanner", "LiDAR"].map((eq) => (
                    <label key={eq} className="flex items-center gap-1.5 text-xs text-foreground-600 cursor-pointer">
                      <input type="checkbox" defaultChecked className="rounded" /> {eq}
                    </label>
                  ))}
                </div>
              </div>
            </>
          )}

          {step === 3 && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-foreground-700 mb-1">Data de Início *</label>
                  <input type="date" className="w-full px-3 py-2 text-sm border border-background-200/70 rounded-lg focus:outline-none focus:border-primary-400 bg-white" required />
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground-700 mb-1">Previsão de Conclusão</label>
                  <input type="date" className="w-full px-3 py-2 text-sm border border-background-200/70 rounded-lg focus:outline-none focus:border-primary-400 bg-white" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-700 mb-1">Produtos Esperados</label>
                <div className="grid grid-cols-2 gap-1.5">
                  {["Mapa Arbóreo", "Dashboard", "Relatório Técnico", "Base GeoJSON", "Mapa de Risco", "Mapa de Poda", "Mapa de Espécies", "Relatório Fotográfico"].map((p) => (
                    <label key={p} className="flex items-center gap-1.5 text-xs text-foreground-600 cursor-pointer">
                      <input type="checkbox" defaultChecked className="rounded" /> {p}
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-700 mb-1">Observações</label>
                <textarea className="w-full px-3 py-2 text-sm border border-background-200/70 rounded-lg focus:outline-none focus:border-primary-400 bg-white resize-none" rows={3} placeholder="Informações adicionais..."></textarea>
              </div>
            </>
          )}

          {/* Footer */}
          <div className="flex items-center justify-between pt-3 border-t border-background-200/50">
            <button type="button" onClick={() => setStep(Math.max(1, step - 1))} className={`text-xs px-3 py-2 rounded-lg transition-colors cursor-pointer whitespace-nowrap ${step === 1 ? "text-foreground-300 cursor-not-allowed" : "text-foreground-600 hover:bg-background-100"}`} disabled={step === 1}>
              <i className="ri-arrow-left-s-line mr-1"></i>Anterior
            </button>
            {step < 3 ? (
              <button type="button" onClick={() => setStep(step + 1)} className="btn-primary text-xs px-4 py-2 cursor-pointer whitespace-nowrap">
                Próximo<i className="ri-arrow-right-s-line ml-1"></i>
              </button>
            ) : (
              <button type="submit" disabled={submitted} className="btn-primary text-xs px-4 py-2 cursor-pointer whitespace-nowrap">
                {submitted ? (
                  <><i className="ri-loader-4-line animate-spin mr-1"></i>Criando...</>
                ) : (
                  <><i className="ri-check-line mr-1"></i>Criar Ação</>
                )}
              </button>
            )}
          </div>

          {submitted && (
            <div className="slide-up p-3 rounded-lg bg-green-50 border border-green-200 text-sm text-green-700 flex items-center gap-2">
              <i className="ri-check-double-line text-green-600"></i>
              Ação criada com sucesso! Redirecionando...
            </div>
          )}
        </form>
      </div>
    </div>
  );
}